import React, { useEffect, useRef, useState } from 'react';

import { Icon } from '@rbilabs/universal-components';
import { Image } from 'expo-image';
import { VideoView, useVideoPlayer } from 'expo-video';
import { useAppTakeoverCart } from 'features/app-takeover/hooks/use-app-takeover-cart';
import { takeoverAudioMuted } from 'features/app-takeover/utils/play-audio-once';
import { logButtonClick } from 'features/navigation/utils/analytics';
import { Modal, StyleSheet, TouchableOpacity, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { withTypography } from 'components/ui';
import { useAppStateChangeListener } from 'hooks/use-app-state-change-listener';
import {
  ClickEventComponentNames,
  CustomEventNames,
  EventTypes,
  logRBIEvent,
} from 'state/amplitude';
import { t } from 'state/intl/translations';
import { primitive } from 'styles/constants';
import { Deferred } from 'utils/Deferred';
import { isWeb, region } from 'utils/environment';
import { Region } from 'utils/environment/types';

const VOLUME_FADE_DURATION_MS = 2000;
const VOLUME_FADE_INTERVAL_MS = 50;

const getPaymentProcessLoadingViewportStyle = (web: boolean) =>
  web ? { height: '100dvh' as never, maxHeight: '100dvh' as never } : null;

export const PaymentProcessLoading = () => {
  const {
    paymentProcessingImage: takeoverCheckoutImage,
    paymentProcessingVideo: takeoverCheckoutVideo,
    paymentProcessingBackgroundColor: takeoverBgColor,
  } = useAppTakeoverCart();
  const animationCompleteDeferred = useRef(new Deferred());
  const { top } = useSafeAreaInsets();
  const topSafeArea = top || 10;
  const [allowsDismiss, setAllowsDismiss] = useState(false);
  const fadeTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const playerRef = useRef<ReturnType<typeof useVideoPlayer> | null>(null);
  const isComponentMountedRef = useRef(true);

  const clearFadeTimer = () => {
    if (fadeTimerRef.current) {
      clearInterval(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      isComponentMountedRef.current = false;
      clearFadeTimer();
      playerRef.current = null;
    };
  }, []);

  const player = useVideoPlayer(takeoverCheckoutVideo ?? null, player => {
    if (!takeoverCheckoutVideo) {
      return;
    }

    player.loop = true;
    player.audioMixingMode = 'mixWithOthers';
    playerRef.current = player;
    let loopCount = 0;
    let hasStartedVolumeFade = false;

    const fadeVolumeToZero = () => {
      if (hasStartedVolumeFade || player.muted) {
        return;
      }
      hasStartedVolumeFade = true;
      clearFadeTimer();
      const startingVolume =
        typeof player.volume === 'number' && Number.isFinite(player.volume) ? player.volume : 1;
      const totalSteps = Math.max(1, Math.round(VOLUME_FADE_DURATION_MS / VOLUME_FADE_INTERVAL_MS));
      let currentStep = 0;

      fadeTimerRef.current = setInterval(() => {
        const activePlayer = playerRef.current;
        if (!isComponentMountedRef.current || !activePlayer) {
          clearFadeTimer();
          return;
        }

        currentStep += 1;
        const progress = Math.min(currentStep / totalSteps, 1);
        const nextVolume = startingVolume * (1 - progress);
        activePlayer.volume = Math.max(0, nextVolume);

        if (progress >= 1) {
          clearFadeTimer();
        }
      }, VOLUME_FADE_INTERVAL_MS);
    };

    if (region === Region.CA || takeoverAudioMuted.current) {
      player.muted = true;
    }

    if (isWeb) {
      // web requires a timeout due to a bug, maybe could remove in SDK 54 https://github.com/expo/expo/issues/36350
      setTimeout(() => player.play(), 100);
    } else {
      player.play();
    }

    player.addListener('playToEnd', () => {
      loopCount += 1;
      if (loopCount === 2) {
        fadeVolumeToZero();
      }
      animationCompleteDeferred.current.resolve();
    });
    player.addListener('statusChange', status => {
      if (status.error) {
        animationCompleteDeferred.current.resolve();
      }
      if (status.status === 'idle') {
        clearFadeTimer();
      }
    });
    // ensure promise resolves if all else fails (with a 1s extra buffer)
    const durationSec = Number.isFinite(player.duration) ? player.duration : 5; // fallback to 5s if undefined
    setTimeout(() => animationCompleteDeferred.current.resolve(), (durationSec + 1) * 1000);
  });

  const [isHidden, setIsHidden] = useState(false);

  // keep video going if app/website loses focus and comes back
  const {
    addAppStateChangeListeners: onFocusListener,
    removeAppStateChangeListeners: onFocusRemoveListener,
  } = useAppStateChangeListener();
  useEffect(() => {
    onFocusListener(() => {
      if (!isHidden && takeoverCheckoutVideo) {
        player.play();
      }
    });
    return () => {
      onFocusRemoveListener();
    };
  }, [onFocusListener, onFocusRemoveListener, player, isHidden, takeoverCheckoutVideo]);

  // show modal dismiss button after 15s as a failsafe if something unexpected happens and/or the user feels stuck
  useEffect(() => {
    logRBIEvent({
      name: CustomEventNames.MODAL_APPEARANCE,
      type: EventTypes.Other,
      attributes: {
        Message: 'Takeover order processing animation modal',
      },
    });

    const timeout = setTimeout(() => setAllowsDismiss(true), 8000);
    return () => clearTimeout(timeout);
  }, []);

  if (isHidden) {
    return null;
  }

  return (
    <Modal visible>
      <View
        testID="payment-processing-container"
        style={[
          styles.container,
          {
            backgroundColor: takeoverBgColor,
            ...getPaymentProcessLoadingViewportStyle(isWeb),
          },
        ]}
      >
        {allowsDismiss && (
          <TouchableOpacity
            onPress={() => {
              if (takeoverCheckoutVideo) {
                player.pause();
              }
              setIsHidden(true);
              logButtonClick({
                attributes: {
                  component: ClickEventComponentNames.BUTTON,
                  name: `Close payment processing animation`,
                },
              });
            }}
            style={[styles.dismissButton, { top: topSafeArea }]}
            hitSlop={15}
            accessibilityLabel={t('close')}
            accessibilityRole="button"
          >
            <Icon variant="cancel" color={primitive.$mayoEggWhite} />
          </TouchableOpacity>
        )}
        {takeoverCheckoutVideo && (
          <View style={[styles.messageContainer, { top: topSafeArea }]}>
            <View style={styles.messageBox}>
              <Message>{t('cartCardLoadingMessage')}</Message>
            </View>
          </View>
        )}
        {takeoverCheckoutVideo ? (
          <VideoView
            style={styles.media}
            // @ts-ignore - VideoPlayer type mismatch between native and web
            player={player}
            nativeControls={false}
            allowsPictureInPicture={false}
            playsInline
            contentFit="cover"
            testID="payment-processing-video"
          />
        ) : takeoverCheckoutImage ? (
          <Image
            accessibilityLabel={t('cartCardLoadingMessage')}
            contentFit="cover"
            source={takeoverCheckoutImage}
            style={styles.media}
            testID="payment-processing-image"
          />
        ) : null}
      </View>
    </Modal>
  );
};

export const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  dismissButton: {
    position: 'absolute',
    right: 10,
    zIndex: 2,
  },
  messageContainer: {
    zIndex: 1,
    position: 'absolute',
    right: 0,
    left: 0,
    alignItems: 'center',
  },
  messageBox: {
    width: 140,
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderRadius: primitive.$borderRadius,
    backgroundColor: primitive.$mayoEggWhite,
  },
  media: {
    flex: 1,
  },
});

export const Message = withTypography({
  variant: 'headerTwo',
  textAlign: 'center',
  mb: '$0',
  lineHeight: 27,
});
